import { Button } from '@suscri/ui';
import { Users, UserPlus, Trash2, LogOut, Check, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PageHeader } from '../components/ui/PageHeader';
import { useToast } from '../lib/toast';
import { trpc } from '../lib/trpc';
import { getActiveWorkspace, setActiveWorkspace } from '../lib/workspace';

// Equipos (M7): lado dueño (invitar/gestionar, requiere plan Business) y
// lado miembro (aceptar invitaciones, cambiar de espacio, abandonar).
export function TeamPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const utils = trpc.useUtils();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'EDITOR' | 'VIEWER'>('EDITOR');

  const { data: team } = trpc.teams.myTeam.useQuery();
  const { data: invitations } = trpc.teams.myInvitations.useQuery();
  const { data: workspaces } = trpc.teams.myWorkspaces.useQuery();

  const refresh = () => {
    utils.teams.invalidate();
  };

  const handleError = (error: unknown) => {
    const message = error instanceof Error ? error.message : '';
    if (message.includes('PLAN_REQUIRED')) {
      toast.error(
        t(
          'Los equipos son una función del plan Business. Mejora tu plan para usarlos.'
        )
      );
    } else {
      toast.error(message || t('No se pudo completar la acción.'));
    }
  };

  const invite = trpc.teams.invite.useMutation({
    onSuccess: () => {
      toast.success(t('Invitación enviada.'));
      setInviteEmail('');
      refresh();
    },
    onError: handleError,
  });
  const removeMember = trpc.teams.remove.useMutation({
    onSuccess: refresh,
    onError: handleError,
  });
  const updateRole = trpc.teams.updateRole.useMutation({
    onSuccess: refresh,
    onError: handleError,
  });
  const accept = trpc.teams.accept.useMutation({
    onSuccess: () => {
      toast.success(
        t(
          'Invitación aceptada. Ya puedes cambiar de espacio desde el selector superior.'
        )
      );
      refresh();
    },
    onError: handleError,
  });
  const decline = trpc.teams.decline.useMutation({
    onSuccess: refresh,
    onError: handleError,
  });
  const leave = trpc.teams.leave.useMutation({
    onSuccess: () => {
      const active = getActiveWorkspace();
      refresh();
      // Si abandono el espacio en el que estaba trabajando, vuelvo al mío
      if (active) setActiveWorkspace(null);
    },
    onError: handleError,
  });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('Colaboración')}
        title={t('Equipo')}
        description={t('Comparte tu espacio con otras personas (plan Business) o gestiona los espacios a los que perteneces')}
      />

      {/* Invitaciones pendientes hacia mí */}
      {invitations && invitations.length > 0 && (
        <div className="rounded-xl border-2 border-violet-200 bg-violet-50/60 p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            {t('Invitaciones pendientes')}
          </h2>
          <div className="space-y-3">
            {invitations.map(inv => (
              <div
                key={inv.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white p-4 shadow-sm"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {inv.ownerName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t('Te invita como')}{' '}
                    {inv.role === 'VIEWER' ? t('lector') : t('editor')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => accept.mutate({ inviteId: inv.id })}
                    disabled={accept.isPending}
                  >
                    <Check className="mr-1 h-4 w-4" /> {t('Aceptar')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => decline.mutate({ inviteId: inv.id })}
                  >
                    <X className="mr-1 h-4 w-4" /> {t('Rechazar')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mi equipo (lado dueño) */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 shadow-md">
            <Users className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('Mi equipo')}
            </h2>
            <p className="text-xs text-gray-500">
              {t(
                'Las personas invitadas podrán ver o editar tus suscripciones, proveedores, categorías e informes. Función del plan'
              )}{' '}
              <Link to="/plan" className="text-blue-600 hover:underline">
                Business
              </Link>
              .
            </p>
          </div>
        </div>

        <form
          className="mb-6 flex flex-wrap items-end gap-3"
          onSubmit={e => {
            e.preventDefault();
            if (!inviteEmail) return;
            invite.mutate({ email: inviteEmail, role: inviteRole });
          }}
        >
          <div className="flex-1 min-w-[220px]">
            <label className="mb-1 block text-xs font-semibold text-gray-500">
              {t('Email')}
            </label>
            <input
              type="email"
              required
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="persona@ejemplo.com"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-500">
              {t('Rol')}
            </label>
            <select
              value={inviteRole}
              onChange={e =>
                setInviteRole(e.target.value === 'VIEWER' ? 'VIEWER' : 'EDITOR')
              }
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="EDITOR">{t('Editor (ver y modificar)')}</option>
              <option value="VIEWER">{t('Lector (solo ver)')}</option>
            </select>
          </div>
          <Button type="submit" disabled={invite.isPending}>
            <UserPlus className="mr-2 h-4 w-4" />
            {invite.isPending ? t('Invitando…') : t('Invitar')}
          </Button>
        </form>

        {team && team.length > 0 ? (
          <div className="space-y-2">
            {team.map(member => (
              <div
                key={member.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {member.name ?? member.email}
                  </p>
                  <p className="text-xs text-gray-500">
                    {member.email} ·{' '}
                    {member.status === 'invited'
                      ? t('invitación pendiente')
                      : t('activo')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={member.role}
                    onChange={e =>
                      updateRole.mutate({
                        memberId: member.id,
                        role: e.target.value === 'VIEWER' ? 'VIEWER' : 'EDITOR',
                      })
                    }
                    className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                  >
                    <option value="EDITOR">{t('Editor')}</option>
                    <option value="VIEWER">{t('Lector')}</option>
                  </select>
                  <button
                    onClick={() => removeMember.mutate({ memberId: member.id })}
                    className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600"
                    title={t('Quitar del equipo')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            {t('Todavía no has invitado a nadie.')}
          </p>
        )}
      </div>

      {/* Espacios a los que pertenezco */}
      {workspaces && workspaces.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            {t('Espacios a los que pertenezco')}
          </h2>
          <div className="space-y-2">
            {workspaces.map(ws => (
              <div
                key={ws.ownerId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {ws.ownerName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {ws.role === 'VIEWER'
                      ? t('Lector (solo ver)')
                      : t('Editor')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setActiveWorkspace({
                        ownerId: ws.ownerId,
                        ownerName: ws.ownerName,
                        role: ws.role,
                      })
                    }
                  >
                    {t('Entrar en este espacio')}
                  </Button>
                  <button
                    onClick={() => leave.mutate({ ownerId: ws.ownerId })}
                    className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600"
                    title={t('Abandonar el espacio')}
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
